import { api } from './_api-client';
import { ClipPayload } from './clip.service';
import { ProjectFile } from './file.service';
import { FolderQueryParams } from './folders.service';
import { Payment, Subscription } from './payment.service';
import { UpdateEntity, UpdateObjectResult } from './project.service';
import { Story, StoryConfig } from './story.service';

export enum RenderStatus {
  pending,
  processing,
  rendered,
  error,
}

export interface FinalCut {
  accessType: number;
  accessedAt: string;
  createdAt: string;
  duration: Seconds;
  errorMessageForUser: string | null;
  expirationTime: number | null;
  folderId: FolderId | null;
  frameRate: number;
  height: number;
  id: FinalcutId;
  itCanRemove: boolean;
  itCanShare: boolean;
  itCanUpdate: boolean;
  mediaOriginDate: string;
  name: string;
  size: number;
  status: RenderStatus;
  storyId: StoryId;
  thumbnailUrl: Url | null;
  updatedAt: string | null;
  width: number;
  payment: Payment | null;
  subscription: Subscription | null;
}

export interface FinalCutVersion {
  duration: number;
  fileName: string;
  frameRate: number;
  id: FinalcutId;
  mediaOriginDate: string;
  posterKey: string | null;
  size: number;
  status: RenderStatus;
  url: Url | null;
  height: number | null;
  width: number | null;
  payment: Payment | null;
  subscription: Subscription | null;
}

export interface FinalCutData {
  beatMarkerArray: MarkerId[];
  clips: Record<ClipId, ClipPayload>;
  finalCut: {
    accessType: number;
    accessedAt: string;
    createdAt: string;
    errorMessageForUser: string | null;
    expirationTime: number | null;
    folderId: number | null;
    id: FinalcutId;
    itCanRemove: boolean;
    itCanShare: boolean;
    itCanUpdate: boolean;
    name: string;
    storyId: StoryId;
    updatedAt: string | null;
  };
  finalCutConfig: StoryConfig;
  finalCutVersions: FinalCutVersion[];
  storyBoardConfig: CellClip[];
  mediaFiles: ProjectFile[];
}

export interface ExecutionResultDto<T = undefined> {
  executionResult: boolean;
  data?: T;
}

/* S E R V I C E */

export const getFinalCut = (finalcutId: FinalcutId) => {
  return api.get<FinalCutData>(`/FinalCut/GetFinalCut?finalCutId=${finalcutId}`);
};

export const getStoryFinalCuts = (storyId: StoryId, { folderId, allActive }: FolderQueryParams) => {
  const params = new URLSearchParams({ storyId: String(storyId) });
  if (folderId !== undefined) params.append('folderId', String(folderId));
  if (allActive !== undefined) params.append('allActive', String(allActive));
  return api.get<FinalCut[]>(`/FinalCut/GetStoryFinalCuts?${params.toString()}`);
};

export const removeFinalCut = (finalcutId: FinalcutId) => {
  return api.delete<boolean>(`/FinalCut/RemoveFinalCut?finalCutId=${finalcutId}`);
};

export const updateFinalcut = (payload: UpdateEntity) => {
  return api.patch<UpdateObjectResult>('/FinalCut/UpdateFinalCut', { name: payload.name, finalCutId: payload.id });
};

export const anyProcessingFinalCuts = () => {
  return api.get<boolean>(`/FinalCut/AnyProcessingFinalCuts`);
};

export const submitRender = (storyId: StoryId) => {
  return api.post<ExecutionResultDto<number>>(`/FinalCut/SubmitRender`, { storyId: storyId });
};

export const updateItCanShare = (finalcutId: FinalcutId) => {
  return api.patch<Story>(`/FinalCut/UpdateItCanShare?finalCutId=${finalcutId}`);
};

export const reRenderFailedFinalCut = (finalcutId: FinalcutId) => {
  return api.patch(`/FinalCut/ReRenderFailedFinalCut?finalCutId=${finalcutId}`);
};
